import { useState, useRef } from "react";
import { resizeImage } from "@lib/helpers";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  FreeMode,
  Navigation,
  Keyboard,
  Thumbs,
  Zoom,
} from "swiper/modules";
import CarouselNavArrow from "@components/Icons/CarouselNavArrow";
import "swiper/css";
import "swiper/css/zoom";
import "swiper/css/pagination";
import getConfig from "next/config";

const {
  publicRuntimeConfig: { settings },
} = getConfig();

const InLightboxGallery = ({
  images,
  title,
  onSlideClick,
  onSlideChange,
  currentSlide,
}) => {
  const singleSlide = images.length <= 1;
  const mainSwiper = useRef(null);

  const [thumbsSwiper, setThumbsSwiper] = useState(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);
  const [prevEl, setPrevEl] = useState(null);
  const [nextEl, setNextEl] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);

  return (
    <div className="flex flex-col-reverse sm:flex-row justify-around w-full relative">
      {/* thumbnail slider */}
      <div className="sm:pl-[20px] flex flex-col">
        <div className="mx-auto mb-[6px]">
          <span className="text-sm md:text-base font-normal md:font-medium">{`${
            currentSlide + 1
          } din ${images.length}`}</span>
        </div>

        <Swiper
          className="w-full px-[15px] sm:px-0 mx-auto sm:mx-0"
          direction="vertical"
          onSwiper={(swiper) => {
            setThumbsSwiper(swiper);
          }}
          loop={false}
          initialSlide={currentSlide}
          modules={[Navigation, Thumbs]}
          onSlideChange={({ isBeginning, isEnd }) => {
            setIsBeginning(isBeginning);
            setIsEnd(isEnd);
          }}
          breakpoints={{
            0: {
              direction: "horizontal",
              slidesPerView: 4,
              slidesPerGroup: 3,
              spaceBetween: 6,
            },
            640: {
              direction: "vertical",
              slidesPerView: "auto",
              slidesPerGroup: 3,
              spaceBetween: 10,
            },
          }}
        >
          {images.map((image, index) => (
            <SwiperSlide
              key={index}
              className="h-full min-w-[80px] sm:!h-[80px] sm:w-[80px]"
            >
              <div
                className={`p-2 md:p-2.5 flex justify-center border rounded-bl-lg rounded-tr-lg cursor-pointer select-none ${
                  currentSlide === index ? "border-yellow" : "border-gray-900"
                }`}
              >
                <img
                  src={resizeImage(image.file, "80x80")}
                  className="block aspect-square object-contain select-none"
                  alt=""
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* main image slider */}
      <Swiper
        className="w-full sm:w-[calc(100%-120px)] relative sm:ml-20px"
        ref={mainSwiper}
        initialSlide={currentSlide}
        slidesPerView={1}
        spaceBetween={10}
        loop={false}
        keyboard={true}
        thumbs={{ swiper: thumbsSwiper }}
        modules={[FreeMode, Thumbs, Zoom, Navigation, Keyboard]}
        onRealIndexChange={({ zoom, realIndex }) => {
          zoom.out();
          onSlideChange(realIndex);
        }}
        onSlideChange={({ isBeginning, isEnd }) => {
          setIsBeginning(isBeginning);
          setIsEnd(isEnd);
        }}
        navigation={{
          nextEl,
          prevEl,
        }}
        zoom={{ maxRatio: 2 }}
        onClick={(swiper) => {
          swiper.zoom.toggle();
          setIsZoomed(!isZoomed);
        }}
      >
        {images.map((image, index) => (
          <SwiperSlide
            className={`${isZoomed ? "!cursor-zoom-out" : "!cursor-zoom-in"}`}
            key={index}
          >
            <div className="swiper-zoom-container mx-auto aspect-square w-full">
              <img
                onClick={onSlideClick}
                src={`${settings.cdn}${image.file}`}
                alt={title}
              />
            </div>
          </SwiperSlide>
        ))}

        {!singleSlide && (
          <>
            <div className="hidden sm:block absolute left-[20px] z-10 top-[50%]">
              <PreviousButton setPrevEl={setPrevEl} isBeginning={isBeginning} />
            </div>

            <div className="hidden sm:block absolute right-[20px] z-10 top-[50%]">
              <NextButton setNextEl={setNextEl} isEnd={isEnd} />
            </div>
          </>
        )}
      </Swiper>
    </div>
  );
};

const PreviousButton = ({ setPrevEl, isBeginning }) => (
  <div
    ref={(node) => setPrevEl(node)}
    className={`w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-1300 ${
      isBeginning ? "opacity-50 cursor-default" : "opacity-90 cursor-pointer"
    }`}
  >
    <CarouselNavArrow />
  </div>
);

const NextButton = ({ setNextEl, isEnd }) => (
  <div
    ref={(node) => setNextEl(node)}
    className={`w-8 h-8 md:w-10 md:h-10 rounded-full bg-gray-1300 ${
      isEnd ? "opacity-50 cursor-default" : "opacity-90 cursor-pointer"
    }`}
  >
    <CarouselNavArrow direction="right" />
  </div>
);

export default InLightboxGallery;
